import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  Plus, 
  Search, 
  CreditCard, 
  CheckCircle,
  AlertCircle,
  ChevronDown,
  X
} from 'lucide-react';
import { getAllDebts, getAllUsers, getAllDebtTypes, createDebt, createTorahDebt, updateDebt, addPayment, createDebtType, deleteDebt } from '../services/api';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Debt, User as UserType, DebtType } from '../types';
import AddDebtModal from '../components/AddDebtModal';
import { TORAH_PORTIONS, HOLIDAYS } from '../data/torahData';

const DebtsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    align-items: stretch;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
  
  @media (max-width: 768px) {
    order: 3; /* move below add button */
    width: 100%;
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 20px 12px 45px;
  border: 2px solid #e1e5e9;
  border-radius: 25px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  size: 20px;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
  
  @media (max-width: 768px) {
    order: 2;
    width: fit-content;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 10px 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const DebtsTable = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 0.5fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e1e5e9;
  
  @media (max-width: 768px) {
    grid-template-columns: 0.5fr 1.5fr 0.8fr 0.8fr 0.8fr 1fr; /* reduce column widths */
    gap: 10px;
    padding: 15px;
    
    & > div:nth-child(4), /* נגבה */
    & > div:nth-child(5)  /* יתרה */ {
      display: none;
    }
  }
`;

const TableRow = styled.div<{ $isPaid: boolean }>`
  display: grid;
  grid-template-columns: 0.5fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
  opacity: ${props => props.$isPaid ? 0.6 : 1};
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 0.5fr 1.5fr 0.8fr 0.8fr 0.8fr 1fr; /* reduce column widths */
    gap: 10px;
    padding: 15px;
    
    & > div:nth-child(4), /* נגבה */
    & > div:nth-child(5)  /* יתרה */ {
      display: none;
    }
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const MemberName = styled.div`
  font-weight: 600;
  color: #333;
`;

const Amount = styled.div<{ type: 'debt' | 'paid' | 'remaining' }>`
  font-weight: 600;
  color: ${props => 
    props.type === 'debt' ? '#333' :
    props.type === 'paid' ? '#28a745' : '#dc3545'
  };
`;

const StatusBadge = styled.div<{ $isPaid: boolean }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$isPaid ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$isPaid ? '#155724' : '#721c24'};
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionSelect = styled.select`
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  background: white;
  color: #333;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &:hover {
    border-color: #667eea;
  }
  
  option {
    padding: 8px;
  }
`;

const Modal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SearchableSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchableInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const DropdownButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const DropdownList = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e1e5e9;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled.div<{ $isSelected: boolean }>`
  padding: 12px;
  cursor: pointer;
  background: ${props => props.$isSelected ? '#f0f8ff' : 'white'};
  color: ${props => props.$isSelected ? '#667eea' : '#333'};
  border-bottom: 1px solid #f0f0f0;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SelectedMember = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 8px;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: #666;
  
  &:hover {
    color: #e74c3c;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 2px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const Debts: React.FC = () => {
  const [debts, setDebts] = useState<Debt[]>([]);
  const [filteredDebts, setFilteredDebts] = useState<Debt[]>([]);
  const [displayedDebts, setDisplayedDebts] = useState<Debt[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [members, setMembers] = useState<UserType[]>([]);
  const [debtTypes, setDebtTypes] = useState<DebtType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTorahModalOpen, setIsTorahModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [editingDebt, setEditingDebt] = useState<Debt | null>(null);
  const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingDebt, setViewingDebt] = useState<Debt | null>(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    memberId: '',
    debtTypeId: '',
    amount: 0,
    description: '',
    dueDate: ''
  });

  // Searchable select states
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<UserType | null>(null);
  const memberDropdownRef = useRef<HTMLDivElement>(null);

  // Debt type search states
  const [debtTypeSearchTerm, setDebtTypeSearchTerm] = useState('');
  const [isDebtTypeDropdownOpen, setIsDebtTypeDropdownOpen] = useState(false);
  const [selectedDebtType, setSelectedDebtType] = useState<DebtType | null>(null);
  const debtTypeDropdownRef = useRef<HTMLDivElement>(null);
  const [isAddDebtTypeModalOpen, setIsAddDebtTypeModalOpen] = useState(false);
  const [newDebtTypeName, setNewDebtTypeName] = useState('');

  // Bulk payment states
  const [selectedDebtIds, setSelectedDebtIds] = useState<string[]>([]);
  const [isBulkPaymentModalOpen, setIsBulkPaymentModalOpen] = useState(false);
  const [bulkPaymentData, setBulkPaymentData] = useState({
    paymentMethod: 'cash' as 'cash' | 'credit' | 'transfer',
    notes: ''
  });

  // Delete debt states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [debtToDelete, setDebtToDelete] = useState<Debt | null>(null);

  const [paymentData, setPaymentData] = useState({
    amount: 0,
    paymentMethod: 'cash' as 'cash' | 'credit' | 'transfer',
    notes: ''
  });

  useEffect(() => {
    loadData();
    
        // בצע מיגרציה ועדכון תאריכים אוטומטית
        const performMigrations = async () => {
          try {
            // יבא את הפונקציות
            const { migrateExistingDebts, updateHebrewDates } = await import('../services/api');
            
            // בצע מיגרציה
            await migrateExistingDebts();
            console.log('מיגרציה הושלמה');
            
            // עדכן תאריכים עבריים
            await updateHebrewDates();
            console.log('עדכון תאריכים הושלם');
            
            
            // רענן את הנתונים
            await loadData();
          } catch (error) {
            console.error('שגיאה במיגרציה:', error);
          }
        };
    
    performMigrations();
  }, []);

  useEffect(() => {
    let filtered = debts;

    if (searchTerm) {
      filtered = filtered.filter(debt => {
        const member = members.find(m => m.id === debt.memberId);
        return member && 
          (`${member.name} ${member.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
           member.phone.includes(searchTerm));
      });
    }

    if (statusFilter === 'paid') {
      filtered = filtered.filter(debt => debt.isPaid);
    } else if (statusFilter === 'unpaid') {
      filtered = filtered.filter(debt => !debt.isPaid);
    }

    // Sort by creation date - newest first
    filtered = filtered.sort((a, b) => {
      try {
        let dateA, dateB;
        
        // Handle Firestore Timestamp
        if (a.createdAt && typeof a.createdAt === 'object' && 'toDate' in a.createdAt) {
          dateA = a.createdAt.toDate();
        } else if (a.createdAt instanceof Date) {
          dateA = a.createdAt;
        } else {
          dateA = new Date(a.createdAt);
        }
        
        if (b.createdAt && typeof b.createdAt === 'object' && 'toDate' in b.createdAt) {
          dateB = b.createdAt.toDate();
        } else if (b.createdAt instanceof Date) {
          dateB = b.createdAt;
        } else {
          dateB = new Date(b.createdAt);
        }
        
        return dateB.getTime() - dateA.getTime(); // Newest first
      } catch (error) {
        console.warn('Error sorting debts by date:', error);
        return 0;
      }
    });

    setFilteredDebts(filtered);
  }, [debts, members, searchTerm, statusFilter]);

  // Update displayed debts when filtered debts change
  useEffect(() => {
    const initialDebts = filteredDebts.slice(0, 25);
    setDisplayedDebts(initialDebts);
    setHasMore(filteredDebts.length > 25);
  }, [filteredDebts]);

  const loadData = async () => {
    try {
      const [debtsData, membersData, debtTypesData] = await Promise.all([
        getAllDebts(),
        getAllUsers(),
        getAllDebtTypes()
      ]);
      
      setDebts(debtsData);
      setMembers(membersData);
      setDebtTypes(debtTypesData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const loadMoreDebts = () => {
    setLoadingMore(true);
    
    // Simulate loading delay
    setTimeout(() => {
      const currentLength = displayedDebts.length;
      const nextBatch = filteredDebts.slice(currentLength, currentLength + 25);
      
      setDisplayedDebts(prev => [...prev, ...nextBatch]);
      setHasMore(currentLength + 25 < filteredDebts.length);
      setLoadingMore(false);
    }, 500);
  };

  const getMemberName = (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    return member ? `${member.name} ${member.lastName}` : 'מתפלל נמחק';
  };

  // Sort members by name
  const sortedMembers = [...members].sort((a, b) => {
    const nameA = `${a.name} ${a.lastName}`.toLowerCase();
    const nameB = `${b.name} ${b.lastName}`.toLowerCase();
    return nameA.localeCompare(nameB, 'he');
  });

  // Filter members based on search term
  const filteredMembers = sortedMembers.filter(member => {
    const fullName = `${member.name} ${member.lastName}`.toLowerCase();
    const phone = member.phone;
    return fullName.includes(memberSearchTerm.toLowerCase()) || 
           phone.includes(memberSearchTerm);
  });

  // Sort debt types alphabetically
  const sortedDebtTypes = [...debtTypes].sort((a, b) => {
    return a.name.localeCompare(b.name, 'he');
  });

  // Filter debt types based on search term
  const filteredDebtTypes = sortedDebtTypes.filter(debtType => {
    return debtType.name.toLowerCase().includes(debtTypeSearchTerm.toLowerCase());
  });

  // Handle member selection
  const handleMemberSelect = (member: UserType) => {
    setSelectedMember(member);
    setFormData({ ...formData, memberId: member.id });
    setMemberSearchTerm(`${member.name} ${member.lastName}`);
    setIsMemberDropdownOpen(false);
  };

  // Clear selected member
  const clearSelectedMember = () => {
    setSelectedMember(null);
    setFormData({ ...formData, memberId: '' });
    setMemberSearchTerm('');
  };

  // Handle debt type selection
  const handleDebtTypeSelect = (debtType: DebtType) => {
    setSelectedDebtType(debtType);
    setFormData({ ...formData, debtTypeId: debtType.id });
    setDebtTypeSearchTerm(debtType.name);
    setIsDebtTypeDropdownOpen(false);
  };

  // Clear selected debt type
  const clearSelectedDebtType = () => {
    setSelectedDebtType(null);
    setFormData({ ...formData, debtTypeId: '' });
    setDebtTypeSearchTerm('');
  };

  // Handle adding new debt type
  const handleAddDebtType = async () => {
    if (!newDebtTypeName.trim()) {
      alert('אנא הכנס שם לסוג החוב');
      return;
    }

    // Check if debt type already exists (case insensitive)
    const trimmedName = newDebtTypeName.trim();
    const existingDebtType = debtTypes.find(dt => 
      dt.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingDebtType) {
      alert('סוג החוב כבר קיים במערכת');
      return;
    }

    try {
      const newDebtTypeId = await createDebtType({
        name: trimmedName,
        isActive: true
      });
      
      // Create the new debt type object
      const newDebtType: DebtType = {
        id: newDebtTypeId,
        name: trimmedName,
        isActive: true,
        createdAt: new Date()
      };
      
      setDebtTypes(prev => [...prev, newDebtType]);
      setNewDebtTypeName('');
      setIsAddDebtTypeModalOpen(false);
      alert('סוג החוב נוסף בהצלחה');
    } catch (error) {
      console.error('Error adding debt type:', error);
      alert('שגיאה בהוספת סוג החוב');
    }
  };

  // Bulk payment functions
  const handleDebtSelection = (debtId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedDebtIds(prev => [...prev, debtId]);
    } else {
      setSelectedDebtIds(prev => prev.filter(id => id !== debtId));
    }
  };

  const handleSelectAll = () => {
    setSelectedDebtIds(filteredDebts.map(debt => debt.id));
  };

  const handleClearSelection = () => {
    setSelectedDebtIds([]);
  };

  const handleBulkPayment = async () => {
    if (selectedDebtIds.length === 0) {
      alert('אנא בחר חובות לעדכון');
      return;
    }

    try {
      const selectedDebts = filteredDebts.filter(debt => selectedDebtIds.includes(debt.id));

      // Update each selected debt
      for (const debt of selectedDebts) {
        await addPayment(debt.id, {
          debtId: debt.id,
          amount: debt.remainingAmount,
          paymentMethod: bulkPaymentData.paymentMethod,
          notes: bulkPaymentData.notes || `תשלום קבוצתי - ${selectedDebts.length} חובות`
        });
      }

      await loadData();
      setSelectedDebtIds([]);
      setIsBulkPaymentModalOpen(false);
      setBulkPaymentData({
        paymentMethod: 'cash',
        notes: ''
      });
      alert(`עודכנו ${selectedDebtIds.length} חובות בהצלחה`);
    } catch (error) {
      console.error('Error processing bulk payment:', error);
      alert('שגיאה בעדכון החובות');
    }
  };

  // Handle delete debt
  const handleDeleteDebt = (debt: Debt) => {
    setDebtToDelete(debt);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteDebt = async () => {
    if (!debtToDelete) return;

    try {
      await deleteDebt(debtToDelete.id);
      await loadData();
      setIsDeleteModalOpen(false);
      setDebtToDelete(null);
      alert('החוב נמחק בהצלחה');
    } catch (error) {
      console.error('Error deleting debt:', error);
      alert('שגיאה במחיקת החוב');
    }
  };

  // Handle delete payment
  const handleDeletePayment = async (debtId: string, paymentIndex: number) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את התשלום הזה?')) {
      return;
    }

    try {
      const debtRef = doc(db, 'debts', debtId);
      const debtDoc = await getDoc(debtRef);
      
      if (!debtDoc.exists()) {
        alert('החוב לא נמצא');
        return;
      }
      
      const debtData = debtDoc.data() as Debt;
      const updatedPayments = debtData.payments.filter((_, index) => index !== paymentIndex);
      const totalPaid = updatedPayments.reduce((sum, p) => sum + p.amount, 0);
      
      await updateDoc(debtRef, {
        payments: updatedPayments,
        paidAmount: totalPaid,
        isPaid: totalPaid >= debtData.amount,
        remainingAmount: debtData.amount - totalPaid
      });
      
      await loadData();
      alert('התשלום נמחק בהצלחה');
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('שגיאה במחיקת התשלום');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target as Node)) {
        setIsMemberDropdownOpen(false);
      }
      if (debtTypeDropdownRef.current && !debtTypeDropdownRef.current.contains(event.target as Node)) {
        setIsDebtTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getDebtTypeName = (debtTypeId: string) => {
    const debtType = debtTypes.find(dt => dt.id === debtTypeId);
    return debtType ? debtType.name : 'לא ידוע';
  };

  const handleAddDebt = () => {
    setEditingDebt(null);
    setFormData({
      memberId: '',
      debtTypeId: '',
      amount: 0,
      description: '',
      dueDate: ''
    });
    setSelectedMember(null);
    setMemberSearchTerm('');
    setSelectedDebtType(null);
    setDebtTypeSearchTerm('');
    setIsModalOpen(true);
  };

  const handleSaveTorahDebt = async (debtData: Partial<Debt>) => {
    try {
      await createTorahDebt(debtData as Omit<Debt, 'id' | 'createdAt' | 'payments' | 'remainingAmount'>);
      await loadData();
      // Show a gentle success message
      const toast = document.createElement('div');
      toast.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(135deg, #4CAF50, #45a049);
        color: white;
        padding: 16px 24px;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(76, 175, 80, 0.3);
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        font-size: 16px;
        font-weight: 500;
        z-index: 10000;
        transform: translateX(100%);
        transition: transform 0.3s ease-in-out;
        direction: rtl;
        text-align: right;
        max-width: 300px;
        border: 1px solid rgba(255, 255, 255, 0.2);
      `;
      toast.innerHTML = `
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="font-size: 20px;">✅</div>
          <div>
            <div style="font-weight: 600; margin-bottom: 4px;">החוב התורני נוסף בהצלחה</div>
            <div style="font-size: 14px; opacity: 0.9;">החוב נשמר במערכת</div>
          </div>
        </div>
      `;
      
      document.body.appendChild(toast);
      
      // Animate in
      setTimeout(() => {
        toast.style.transform = 'translateX(0)';
      }, 100);
      
      // Auto remove after 4 seconds
      setTimeout(() => {
        toast.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      }, 4000);
      
    } catch (error) {
      console.error('Error saving Torah debt:', error);
      alert('שגיאה בהוספת החוב');
    }
  };


  const handleEditDebt = (debt: Debt) => {
    setEditingDebt(debt);
    
    // Find and set the selected member
    const member = members.find(m => m.id === debt.memberId);
    setSelectedMember(member || null);
    setMemberSearchTerm(member ? `${member.name} ${member.lastName}` : '');
    
    // Find and set the selected debt type
    const debtType = debtTypes.find(dt => dt.id === debt.debtTypeId);
    setSelectedDebtType(debtType || null);
    setDebtTypeSearchTerm(debtType ? debtType.name : '');
    
    setFormData({
      memberId: debt.memberId,
      debtTypeId: debt.debtTypeId,
      amount: debt.amount,
      description: debt.description || '',
      dueDate: '' // Keep empty for editing
    });
    setIsModalOpen(true);
  };

  const handleSaveDebt = async () => {
    try {
      const debtData = {
        ...formData,
        amount: Number(formData.amount)
        // Remove dueDate from editing - only allow amount and type changes
      };

      if (editingDebt) {
        await updateDebt(editingDebt.id, debtData);
      } else {
        await createDebt({
          ...debtData,
          isPaid: false,
          paidAmount: 0,
          debtCategory: 'shabbat', // Default for regular debts
          prayerTypeId: 'shacharit_regular', // Default
          serviceId: debtData.debtTypeId, // Use debtTypeId as serviceId
          hebrewDate: '',
          gregorianDate: ''
        });
      }
      await loadData();
      setSelectedMember(null);
      setMemberSearchTerm('');
      setSelectedDebtType(null);
      setDebtTypeSearchTerm('');
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving debt:', error);
    }
  };

  const handleAddPayment = (debt: Debt) => {
    setSelectedDebt(debt);
    setPaymentData({
      amount: debt.remainingAmount,
      paymentMethod: 'cash',
      notes: ''
    });
    setIsPaymentModalOpen(true);
  };

  const handleViewDebt = (debt: Debt) => {
    setViewingDebt(debt);
    setIsViewModalOpen(true);
  };

  const handleSavePayment = async () => {
    if (!selectedDebt) return;

    try {
      await addPayment(selectedDebt.id, {
        ...paymentData,
        debtId: selectedDebt.id
      });
      await loadData();
      setIsPaymentModalOpen(false);
    } catch (error) {
      console.error('Error adding payment:', error);
    }
  };

  if (loading) {
    return (
      <DebtsContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          טוען נתונים...
        </div>
      </DebtsContainer>
    );
  }

  return (
    <DebtsContainer>
      <Header>
        <Title>
          <CreditCard size={28} />
          ניהול חייבים
        </Title>
        <SearchBar>
          <SearchInput
            type="text"
            placeholder="חיפוש מתפלל..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon size={20} />
        </SearchBar>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {selectedDebtIds.length > 0 && (
            <>
              <Button
                variant="secondary"
                onClick={() => setIsBulkPaymentModalOpen(true)}
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                עדכן נבחרים ({selectedDebtIds.length})
              </Button>
              <Button
                variant="secondary"
                onClick={handleClearSelection}
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                נקה בחירה
              </Button>
            </>
          )}
          <AddButton onClick={() => setIsTorahModalOpen(true)} style={{ background: '#4CAF50' }}>
            <Plus size={20} />
            הוסף חוב
          </AddButton>
        </div>
      </Header>

      <FilterBar>
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
        >
          <option value="all">כל החובות</option>
          <option value="unpaid">חובות פתוחים</option>
          <option value="paid">חובות סגורים</option>
        </FilterSelect>
      </FilterBar>

      {filteredDebts.length === 0 ? (
        <EmptyState>
          <CreditCard size={48} style={{ marginBottom: '20px', opacity: 0.5 }} />
          <h3>אין חובות במערכת</h3>
          <p>לחץ על "הוסף חוב" כדי להתחיל</p>
        </EmptyState>
      ) : (
        <>
        <DebtsTable>
          <TableHeader>
            <div>
              <input
                type="checkbox"
                checked={selectedDebtIds.length === filteredDebts.length && filteredDebts.length > 0}
                onChange={(e) => e.target.checked ? handleSelectAll() : handleClearSelection()}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
            </div>
            <div>מתפלל</div>
            <div>סוג חוב</div>
            <div>סכום</div>
            <div>נגבה</div>
            <div>יתרה</div>
            <div>סטטוס</div>
            <div>פעולות</div>
          </TableHeader>
          
          {displayedDebts.map((debt) => (
            <TableRow key={debt.id} $isPaid={debt.isPaid}>
              <Cell>
                <input
                  type="checkbox"
                  checked={selectedDebtIds.includes(debt.id)}
                  onChange={(e) => handleDebtSelection(debt.id, e.target.checked)}
                  disabled={debt.isPaid}
                  style={{ 
                    width: '18px', 
                    height: '18px', 
                    cursor: debt.isPaid ? 'not-allowed' : 'pointer',
                    opacity: debt.isPaid ? 0.5 : 1
                  }}
                />
              </Cell>
              <Cell>
                <MemberName>{getMemberName(debt.memberId)}</MemberName>
              </Cell>
              <Cell>
                {debt.debtCategory === 'shabbat' || debt.debtCategory === 'holiday' ? (
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                      {debt.debtCategory === 'shabbat' ? 
                        (debt.torahPortionId ? TORAH_PORTIONS.find(p => p.id === debt.torahPortionId)?.hebrewName : 'פרשה לא ידועה') :
                        (debt.holidayId ? HOLIDAYS.find(h => h.id === debt.holidayId)?.hebrewName : 'חג לא ידוע')
                      }
                    </div>
                    {debt.prayerTypeId && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        {(() => {
                          return debt.prayerTypeId === 'shacharit_early' ? 'שחרית נץ' :
                                 debt.prayerTypeId === 'shacharit_regular' ? 'שחרית רגיל' :
                                 debt.prayerTypeId === 'mincha' ? 'מנחה' :
                                 debt.prayerTypeId === 'other' ? 'אחר' : debt.prayerTypeId;
                        })()}
                      </div>
                    )}
                  </div>
                ) : (
                  getDebtTypeName(debt.debtTypeId)
                )}
              </Cell>
              <Cell>
                <Amount type="debt">₪{(debt.amount || 0).toLocaleString()}</Amount>
              </Cell>
              <Cell>
                <Amount type="paid">₪{(debt.paidAmount || 0).toLocaleString()}</Amount>
              </Cell>
              <Cell>
                <Amount type="remaining">₪{(debt.remainingAmount || 0).toLocaleString()}</Amount>
              </Cell>
              <Cell>
                <StatusBadge $isPaid={debt.isPaid}>
                  {debt.isPaid ? (
                    <>
                      <CheckCircle size={12} />
                      סגור
                    </>
                  ) : (
                    <>
                      <AlertCircle size={12} />
                      פתוח
                    </>
                  )}
                </StatusBadge>
              </Cell>
              <Cell>
                <Actions>
                  <ActionSelect
                    value=""
                    onChange={(e) => {
                      const action = e.target.value;
                      if (action === 'view') {
                        handleViewDebt(debt);
                      } else if (action === 'edit') {
                        handleEditDebt(debt);
                      } else if (action === 'payment' && !debt.isPaid) {
                        handleAddPayment(debt);
                      } else if (action === 'delete') {
                        handleDeleteDebt(debt);
                      }
                      e.target.value = ''; // Reset selection
                    }}
                  >
                    <option value="">פעולה</option>
                    <option value="view">צפה</option>
                    <option value="edit">ערוך</option>
                    {!debt.isPaid && (
                      <option value="payment">הוסף תשלום</option>
                    )}
                    <option value="delete" style={{ color: '#d32f2f' }}>מחק חוב</option>
                  </ActionSelect>
                </Actions>
              </Cell>
            </TableRow>
          ))}
        </DebtsTable>
        
        {/* Load More Button */}
        {hasMore && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '20px',
            padding: '20px'
          }}>
            <Button
              variant="secondary"
              onClick={loadMoreDebts}
              disabled={loadingMore}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                minWidth: '120px'
              }}
            >
              {loadingMore ? 'טוען...' : 'טען עוד'}
            </Button>
          </div>
        )}
        </>
      )}

      {/* Add/Edit Debt Modal */}
      <Modal $isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {editingDebt ? 'ערוך חוב' : 'הוסף חוב חדש'}
            </ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>מתפלל *</Label>
            <SearchableSelectContainer ref={memberDropdownRef}>
              {selectedMember ? (
                <SelectedMember>
                  <span>{selectedMember.name} {selectedMember.lastName}</span>
                  <ClearButton onClick={clearSelectedMember}>
                    <X size={16} />
                  </ClearButton>
                </SelectedMember>
              ) : (
                <>
                  <SearchableInput
                    type="text"
                    placeholder="חיפוש מתפלל לפי שם או טלפון..."
                    value={memberSearchTerm}
                    onChange={(e) => {
                      setMemberSearchTerm(e.target.value);
                      setIsMemberDropdownOpen(true);
                    }}
                    onFocus={() => setIsMemberDropdownOpen(true)}
                    required
                  />
                  <DropdownButton onClick={() => setIsMemberDropdownOpen(!isMemberDropdownOpen)}>
                    <ChevronDown size={20} />
                  </DropdownButton>
                </>
              )}
              
              <DropdownList $isOpen={isMemberDropdownOpen}>
                {filteredMembers.map(member => (
                  <DropdownItem
                    key={member.id}
                    $isSelected={selectedMember?.id === member.id}
                    onClick={() => handleMemberSelect(member)}
                  >
                    <div>
                      <div style={{ fontWeight: '600' }}>
                        {member.name} {member.lastName}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {member.phone}
                      </div>
                    </div>
                  </DropdownItem>
                ))}
                {filteredMembers.length === 0 && (
                  <DropdownItem $isSelected={false}>
                    <div style={{ color: '#666', textAlign: 'center' }}>
                      לא נמצאו מתפללים
                    </div>
                  </DropdownItem>
                )}
              </DropdownList>
            </SearchableSelectContainer>
          </FormGroup>

          {!editingDebt && (
            <FormGroup>
              <Label>סוג חוב *</Label>
              <SearchableSelectContainer ref={debtTypeDropdownRef}>
                {!selectedDebtType ? (
                  <Input
                    type="text"
                    placeholder="חיפוש סוג חוב..."
                    value={debtTypeSearchTerm}
                    onChange={(e) => {
                      setDebtTypeSearchTerm(e.target.value);
                      setIsDebtTypeDropdownOpen(true);
                    }}
                    onFocus={() => setIsDebtTypeDropdownOpen(true)}
                    required
                  />
                ) : (
                  <>
                    <Input
                      type="text"
                      placeholder="חיפוש סוג חוב..."
                      value={debtTypeSearchTerm}
                      onChange={(e) => {
                        setDebtTypeSearchTerm(e.target.value);
                        setIsDebtTypeDropdownOpen(true);
                      }}
                      onFocus={() => setIsDebtTypeDropdownOpen(true)}
                      required
                    />
                    <DropdownButton onClick={() => setIsDebtTypeDropdownOpen(!isDebtTypeDropdownOpen)}>
                      <ChevronDown size={20} />
                    </DropdownButton>
                  </>
                )}
                
                <DropdownList $isOpen={isDebtTypeDropdownOpen}>
                  {filteredDebtTypes.map(debtType => (
                    <DropdownItem
                      key={debtType.id}
                      $isSelected={selectedDebtType?.id === debtType.id}
                      onClick={() => handleDebtTypeSelect(debtType)}
                    >
                      <div style={{ fontWeight: '600' }}>
                        {debtType.name}
                      </div>
                    </DropdownItem>
                  ))}
                  {filteredDebtTypes.length === 0 && (
                    <DropdownItem $isSelected={false}>
                      <div style={{ color: '#666', textAlign: 'center' }}>
                        לא נמצאו סוגי חוב
                      </div>
                    </DropdownItem>
                  )}
                </DropdownList>
                
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setIsAddDebtTypeModalOpen(true)}
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    + הוסף סוג חוב
                  </Button>
                  {selectedDebtType && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={clearSelectedDebtType}
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      נקה
                    </Button>
                  )}
                </div>
              </SearchableSelectContainer>
            </FormGroup>
          )}

          <FormGroup>
            <Label>סכום *</Label>
            <Input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>תיאור</Label>
            <Input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </FormGroup>


          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleSaveDebt}>
              {editingDebt ? 'עדכן' : 'הוסף'}
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>

      {/* Payment Modal */}
      <Modal $isOpen={isPaymentModalOpen} onClick={() => setIsPaymentModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>הוסף תשלום</ModalTitle>
            <CloseButton onClick={() => setIsPaymentModalOpen(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>סכום תשלום *</Label>
            <Input
              type="number"
              value={paymentData.amount}
              onChange={(e) => setPaymentData({ ...paymentData, amount: Number(e.target.value) })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>שיטת תשלום *</Label>
            <Select
              value={paymentData.paymentMethod}
              onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value as any })}
              required
            >
              <option value="cash">מזומן</option>
              <option value="credit">אשראי</option>
              <option value="transfer">העברה</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>הערות</Label>
            <Input
              type="text"
              value={paymentData.notes}
              onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
            />
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsPaymentModalOpen(false)}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleSavePayment}>
              הוסף תשלום
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>

      {/* View Debt Details Modal */}
      <Modal $isOpen={isViewModalOpen} onClick={() => setIsViewModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>פרטי החוב</ModalTitle>
            <CloseButton onClick={() => setIsViewModalOpen(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          {viewingDebt && (
            <div>
              <FormGroup>
                <Label>מתפלל</Label>
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  {getMemberName(viewingDebt.memberId)}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>סוג החוב</Label>
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  {getDebtTypeName(viewingDebt.debtTypeId)}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>סכום החוב</Label>
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                  ₪{(viewingDebt.amount || 0).toLocaleString()}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>סכום ששולם</Label>
                <div style={{ padding: '12px', background: '#e8f5e8', borderRadius: '8px' }}>
                  ₪{(viewingDebt.paidAmount || 0).toLocaleString()}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>יתרה</Label>
                <div style={{ 
                  padding: '12px', 
                  background: viewingDebt.remainingAmount > 0 ? '#ffebee' : '#e8f5e8', 
                  borderRadius: '8px',
                  color: viewingDebt.remainingAmount > 0 ? '#d32f2f' : '#2e7d32',
                  fontWeight: 'bold'
                }}>
                  ₪{(viewingDebt.remainingAmount || 0).toLocaleString()}
                </div>
              </FormGroup>

              {viewingDebt.description && (
                <FormGroup>
                  <Label>תיאור</Label>
                  <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                    {viewingDebt.description}
                  </div>
                </FormGroup>
              )}

              {viewingDebt.dueDate && (
                <FormGroup>
                  <Label>תאריך חוב</Label>
                  <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                    {(() => {
                      try {
                        let date;
                        if (viewingDebt.dueDate && typeof viewingDebt.dueDate === 'object' && 'toDate' in viewingDebt.dueDate) {
                          date = viewingDebt.dueDate.toDate();
                        } else if (viewingDebt.dueDate instanceof Date) {
                          date = viewingDebt.dueDate;
                        } else {
                          date = new Date(viewingDebt.dueDate);
                        }
                        return date.toLocaleDateString('he-IL');
                      } catch (error) {
                        return 'תאריך לא תקין';
                      }
                    })()}
                  </div>
                </FormGroup>
              )}

              {viewingDebt.payments && viewingDebt.payments.length > 0 && (
                <FormGroup>
                  <Label>תשלומים</Label>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {viewingDebt.payments.map((payment, index) => (
                      <div key={index} style={{ 
                        padding: '10px', 
                        margin: '5px 0', 
                        background: '#f8f9fa', 
                        borderRadius: '8px',
                        border: '1px solid #e1e5e9'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <strong>₪{(payment.amount || 0).toLocaleString()}</strong>
                            <span style={{ margin: '0 10px', color: '#666' }}>
                              {payment.paymentMethod === 'cash' ? 'מזומן' : 
                               payment.paymentMethod === 'credit' ? 'אשראי' : 'העברה'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ color: '#666', fontSize: '14px' }}>
                            {(() => {
                              try {
                                let date;
                                if (payment.paymentDate && typeof payment.paymentDate === 'object' && 'toDate' in payment.paymentDate) {
                                  date = payment.paymentDate.toDate();
                                } else if (payment.paymentDate instanceof Date) {
                                  date = payment.paymentDate;
                                } else {
                                  date = new Date(payment.paymentDate);
                                }
                                return date.toLocaleDateString('he-IL');
                              } catch (error) {
                                return 'תאריך לא תקין';
                              }
                            })()}
                            </div>
                            <button
                              onClick={() => handleDeletePayment(viewingDebt.id, index)}
                              style={{
                                background: '#ffebee',
                                color: '#d32f2f',
                                border: '1px solid #ffcdd2',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              מחק
                            </button>
                          </div>
                        </div>
                        {payment.notes && (
                          <div style={{ marginTop: '5px', color: '#666', fontSize: '14px' }}>
                            {payment.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </FormGroup>
              )}

              <ButtonGroup>
                <Button variant="secondary" onClick={() => setIsViewModalOpen(false)}>
                  סגור
                </Button>
              </ButtonGroup>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Add Debt Type Modal */}
      <Modal $isOpen={isAddDebtTypeModalOpen} onClick={() => setIsAddDebtTypeModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <h2>הוסף סוג חוב חדש</h2>
            <CloseButton onClick={() => setIsAddDebtTypeModalOpen(false)}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>שם סוג החוב *</Label>
            <Input
              type="text"
              value={newDebtTypeName}
              onChange={(e) => setNewDebtTypeName(e.target.value)}
              placeholder="הכנס שם לסוג החוב..."
              required
            />
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsAddDebtTypeModalOpen(false)}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleAddDebtType}>
              הוסף
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>

      {/* Bulk Payment Modal */}
      <Modal $isOpen={isBulkPaymentModalOpen} onClick={() => setIsBulkPaymentModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <h2>עדכון קבוצתי - {selectedDebtIds.length} חובות</h2>
            <CloseButton onClick={() => setIsBulkPaymentModalOpen(false)}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>חובות נבחרים</Label>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e1e5e9', borderRadius: '8px', padding: '10px' }}>
              {filteredDebts
                .filter(debt => selectedDebtIds.includes(debt.id))
                .map(debt => (
                  <div key={debt.id} style={{ 
                    padding: '8px', 
                    margin: '4px 0', 
                    background: '#f8f9fa', 
                    borderRadius: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600' }}>{getMemberName(debt.memberId)}</div>
                      <div style={{ fontSize: '14px', color: '#666' }}>{getDebtTypeName(debt.debtTypeId)}</div>
                    </div>
                    <div style={{ fontWeight: '600', color: '#e74c3c' }}>
                      ₪{(debt.remainingAmount || 0).toLocaleString()}
                    </div>
                  </div>
                ))}
            </div>
            <div style={{ 
              marginTop: '10px', 
              padding: '10px', 
              background: '#e8f5e8', 
              borderRadius: '6px',
              textAlign: 'center',
              fontWeight: '600',
              fontSize: '16px'
            }}>
              סכום כולל: ₪{filteredDebts
                .filter(debt => selectedDebtIds.includes(debt.id))
                .reduce((sum, debt) => sum + (debt.remainingAmount || 0), 0)
                .toLocaleString()}
            </div>
          </FormGroup>

          <FormGroup>
            <Label>שיטת תשלום</Label>
            <Select
              value={bulkPaymentData.paymentMethod}
              onChange={(e) => setBulkPaymentData({ ...bulkPaymentData, paymentMethod: e.target.value as any })}
            >
              <option value="cash">מזומן</option>
              <option value="credit">אשראי</option>
              <option value="transfer">העברה בנקאית</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>הערות</Label>
            <textarea
              value={bulkPaymentData.notes}
              onChange={(e) => setBulkPaymentData({ ...bulkPaymentData, notes: e.target.value })}
              placeholder="הערות כלליות לתשלום..."
              rows={3}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e1e5e9',
                borderRadius: '8px',
                fontSize: '16px',
                minHeight: '80px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsBulkPaymentModalOpen(false)}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleBulkPayment}>
              עדכן {selectedDebtIds.length} חובות
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>

      {/* Delete Debt Confirmation Modal */}
      <Modal $isOpen={isDeleteModalOpen} onClick={() => setIsDeleteModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '48px', 
                height: '48px', 
                borderRadius: '50%', 
                background: '#ffebee', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#d32f2f'
              }}>
                <AlertCircle size={24} />
              </div>
              <div>
                <h2 style={{ margin: 0, color: '#d32f2f' }}>מחיקת חוב</h2>
                <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
                  פעולה זו לא ניתנת לביטול
                </p>
              </div>
            </div>
            <CloseButton onClick={() => setIsDeleteModalOpen(false)}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          {debtToDelete && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ 
                padding: '16px', 
                background: '#f8f9fa', 
                borderRadius: '8px',
                border: '1px solid #e1e5e9'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <strong>מתפלל:</strong> {getMemberName(debtToDelete.memberId)}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>סוג חוב:</strong> {getDebtTypeName(debtToDelete.debtTypeId)}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>סכום:</strong> ₪{(debtToDelete.amount || 0).toLocaleString()}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>שולם:</strong> ₪{(debtToDelete.paidAmount || 0).toLocaleString()}
                </div>
                <div style={{ 
                  padding: '8px 12px', 
                  background: debtToDelete.remainingAmount > 0 ? '#ffebee' : '#e8f5e8',
                  borderRadius: '6px',
                  color: debtToDelete.remainingAmount > 0 ? '#d32f2f' : '#2e7d32',
                  fontWeight: '600'
                }}>
                  <strong>יתרה:</strong> ₪{(debtToDelete.remainingAmount || 0).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          <div style={{ 
            padding: '16px', 
            background: '#fff3cd', 
            borderRadius: '8px',
            border: '1px solid #ffeaa7',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <AlertCircle size={16} style={{ color: '#856404' }} />
              <strong style={{ color: '#856404' }}>אזהרה!</strong>
            </div>
            <p style={{ margin: 0, color: '#856404', fontSize: '14px' }}>
              האם אתה בטוח שברצונך למחוק את החוב הזה? פעולה זו תמחק את כל הנתונים הקשורים לחוב הזה כולל כל התשלומים שנרשמו עבורו.
            </p>
          </div>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              ביטול
            </Button>
            <Button 
              variant="primary" 
              onClick={confirmDeleteDebt}
              style={{ 
                background: '#d32f2f', 
                borderColor: '#d32f2f',
                color: 'white'
              }}
            >
              מחק חוב
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>

      {/* Torah Debt Modal */}
      <AddDebtModal
        isOpen={isTorahModalOpen}
        onClose={() => setIsTorahModalOpen(false)}
        onSave={handleSaveTorahDebt}
        members={members}
        debtTypes={debtTypes}
      />
    </DebtsContainer>
  );
};

export default Debts;
